import { ForYouCollectionIds } from 'common/Constants';
import { getActiveSeasonalFavoriteKey, SEASONAL_FAVORITE_COPY, SeasonalFavoriteKey } from 'common/SeasonalFavorites';
import { isNil } from 'lodash';
import { RouteChildrenProps } from 'react-router';
import { DietaryRestriction, DietType, FlaggableIngredient, MerchandisingTag } from 'src/gqlReactTypings.generated.d';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { IconName } from 'web-common/src/shared/design-system/components/Icon';
import { SelectorOption } from 'web-common/src/shared/design-system/components/Selector/Selector';

const INTAKE_ROUTES = [Routes.CONSUMER_MEAL_PLANS_PREFERENCES, Routes.CONSUMER_MEAL_PLANS_CHECKOUT];

/**
 * Whether the user is partway through the intake, where the store holds unsubmitted answers.
 *
 * Every route here must be guarded together: dish selection sits on the checkout route, so
 * covering only preferences leaves it exposed.
 */
export const isInsideIntake = (location: RouteChildrenProps['location']): boolean =>
  locationInRoutes(location, INTAKE_ROUTES);

/** Whether the user is on the preference questions themselves, rather than past them on dish selection. */
export const isInsidePreferences = (location: RouteChildrenProps['location']): boolean =>
  locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_PREFERENCES]);

export const USER_SELECTED_ALL_DISHES_NOTIFICATION_TEXT = `You have selected all your dishes for your this order!`;
export const USER_SELECTED_ALL_SIDES_NOTIFICATION_TEXT = `You have selected all your sides for your this order!`;

export enum MealPlanEntryPoints {
  MPLandingPage = 'MPLandingPage',
  ExploreBanner = 'ExploreBanner',
  ExploreModal = 'ExploreModal',
  HamburgerMenu = 'HamburgerMenu',
  CancelAndResubscribe = 'CancelAndResubscribe',
  ExploreNonSubscriberTab = 'ExploreNonSubscriberTab',
  ExploreMealPlanShefMenu = 'ExploreMealPlanShefMenu',
  MealPlanDishSelection = 'MealPlanDishSelection',
}

export enum MealPlanPreferencesRedirectSource {
  TasteProfileFilterModal = 'TasteProfileFilterModal',
  CuisinePreferencesModal = 'CuisinePreferencesModal',
  DishSelectionStartOver = 'DishSelectionStartOver',
}

type IngredientRestrictionMapping = {
  iconName: IconName;
  restrictionIconName: IconName;
  copy: string;
};

export const INGREDIENTS_FLAGGED_MAPPINGS: Record<FlaggableIngredient, IngredientRestrictionMapping> = {
  [FlaggableIngredient.Beef]: {
    iconName: 'proteinBeef',
    restrictionIconName: 'restrictionBeef',
    copy: 'Beef',
  },
  [FlaggableIngredient.Cilantro]: {
    iconName: 'cilantro',
    restrictionIconName: 'restrictionCilantro',
    copy: 'Cilantro',
  },
  [FlaggableIngredient.Dairy]: {
    iconName: 'dairy',
    restrictionIconName: 'restrictionDairyFree',
    copy: 'Dairy',
  },
  [FlaggableIngredient.Eggs]: {
    iconName: 'proteinEgg',
    restrictionIconName: 'restrictionEggs',
    copy: 'Eggs',
  },
  [FlaggableIngredient.Gluten]: {
    iconName: 'gluten',
    restrictionIconName: 'restrictionGlutenFree',
    copy: 'Gluten',
  },
  [FlaggableIngredient.Mushrooms]: {
    iconName: 'mushrooms',
    restrictionIconName: 'restrictionMushrooms',
    copy: 'Mushrooms',
  },
  [FlaggableIngredient.Nuts]: {
    iconName: 'nuts',
    restrictionIconName: 'restrictionNuts',
    copy: 'Nuts',
  },
  [FlaggableIngredient.Pork]: {
    iconName: 'proteinPork',
    restrictionIconName: 'restrictionPork',
    copy: 'Pork',
  },
  [FlaggableIngredient.Shellfish]: {
    iconName: 'proteinShellfish',
    restrictionIconName: 'restrictionShellfish',
    copy: 'Shellfish',
  },
  [FlaggableIngredient.Soy]: {
    iconName: 'soy',
    restrictionIconName: 'restrictionSoy',
    copy: 'Soy',
  },
  [FlaggableIngredient.Onions]: {
    iconName: 'onions',
    restrictionIconName: 'restrictionOnions',
    copy: 'Onions',
  },
  [FlaggableIngredient.Avocado]: {
    iconName: 'avocado',
    restrictionIconName: 'restrictionAvocado',
    copy: 'Avocado',
  },
  [FlaggableIngredient.Sesame]: {
    iconName: 'sesame',
    restrictionIconName: 'restrictionSesame',
    copy: 'Sesame',
  },
  [FlaggableIngredient.BellPepper]: {
    iconName: 'bellPepper',
    restrictionIconName: 'restrictionBellPepper',
    copy: 'Bell Pepper',
  },
  [FlaggableIngredient.Garlic]: {
    iconName: 'garlic',
    restrictionIconName: 'restrictionGarlic',
    copy: 'Garlic',
  },
};

const AVOID_INGREDIENT_DISPLAY_ORDER: readonly DietaryRestriction[] = [
  DietaryRestriction.Gluten,
  DietaryRestriction.Dairy,
  DietaryRestriction.Nuts,
  DietaryRestriction.Shellfish,
  DietaryRestriction.Beef,
  DietaryRestriction.Pork,
  DietaryRestriction.Eggs,
  DietaryRestriction.Soy,
  DietaryRestriction.Cilantro,
  DietaryRestriction.Mushrooms,
  DietaryRestriction.Onions,
  DietaryRestriction.Avocado,
  DietaryRestriction.Sesame,
  DietaryRestriction.BellPepper,
  DietaryRestriction.Garlic,
];

const FLAGGABLE_INGREDIENT_VALUES: readonly string[] = Object.values(FlaggableIngredient);

const isFlaggableIngredient = (value: string): value is FlaggableIngredient =>
  FLAGGABLE_INGREDIENT_VALUES.includes(value);

/**
 * The structured "ingredients to avoid" options, in display order. Both the intake restrictions
 * step and the dish-selection filter modal render from this list, so a restriction added to
 * the display order appears on both. Label and icon come from {@link INGREDIENTS_FLAGGED_MAPPINGS},
 * whose keys share their string values with these restrictions.
 */
export const AVOID_INGREDIENT_OPTIONS: readonly SelectorOption<DietaryRestriction>[] =
  AVOID_INGREDIENT_DISPLAY_ORDER.flatMap((restriction) => {
    const restrictionValue: string = restriction;
    if (!isFlaggableIngredient(restrictionValue)) return [];
    const { copy, restrictionIconName } = INGREDIENTS_FLAGGED_MAPPINGS[restrictionValue];
    return [{ label: copy, value: restriction, iconName: restrictionIconName }];
  });

const DIET_TYPE_EXCLUDED_RESTRICTIONS: Record<DietType, readonly DietaryRestriction[]> = {
  [DietType.Omnivore]: [],
  [DietType.Pescatarian]: [DietaryRestriction.Beef, DietaryRestriction.Pork],
  [DietType.Vegetarian]: [DietaryRestriction.Beef, DietaryRestriction.Pork, DietaryRestriction.Shellfish],
  [DietType.Vegan]: [
    DietaryRestriction.Beef,
    DietaryRestriction.Pork,
    DietaryRestriction.Shellfish,
    DietaryRestriction.Eggs,
    DietaryRestriction.Dairy,
  ],
  [DietType.Paleo]: [],
  [DietType.Whole30]: [],
  [DietType.Halal]: [DietaryRestriction.Pork],
};

/**
 * Whether the diet type already rules the restriction out, so offering it as an "ingredient to
 * avoid" would be redundant. Shared by the intake restrictions step and the dish-selection
 * filter modal; each surface decides whether to hide or disable the option.
 */
export const isRestrictionExcludedByDietType = (restriction: DietaryRestriction, dietType?: DietType | null): boolean =>
  !isNil(dietType) && DIET_TYPE_EXCLUDED_RESTRICTIONS[dietType].includes(restriction);

const SEASONAL_FAVORITE_TAG_MAP: Record<SeasonalFavoriteKey, MerchandisingTag> = {
  [SeasonalFavoriteKey.SpringFavorites]: MerchandisingTag.SpringFavorites,
  [SeasonalFavoriteKey.SummerFavorites]: MerchandisingTag.SummerFavorites,
  [SeasonalFavoriteKey.FallFavorites]: MerchandisingTag.FallFavorites,
  [SeasonalFavoriteKey.WinterFavorites]: MerchandisingTag.WinterFavorites,
};

export interface ActiveSeasonalFavoriteMetadata {
  key: SeasonalFavoriteKey;
  merchandisingTag: MerchandisingTag;
  filterLabel: string;
}

export const getActiveSeasonalFavoriteMetadata = (referenceDate?: Date): ActiveSeasonalFavoriteMetadata | null => {
  const key = getActiveSeasonalFavoriteKey({ referenceDate });
  if (!key) {
    return null;
  }

  return {
    key,
    merchandisingTag: SEASONAL_FAVORITE_TAG_MAP[key],
    filterLabel: SEASONAL_FAVORITE_COPY[key].filterLabel,
  };
};

export const MERCHANDISING_TAG_MAPPINGS: Record<MerchandisingTag, string> = {
  [MerchandisingTag.SpringFavorites]: 'Spring flavors',
  [MerchandisingTag.SummerFavorites]: 'Summer flavors',
  [MerchandisingTag.FallFavorites]: 'Fall flavors',
  [MerchandisingTag.WinterFavorites]: 'Winter flavors',
  [MerchandisingTag.FamilyFriendly]: 'Family-friendly',
  [MerchandisingTag.NewFresh]: 'New & fresh',
  [MerchandisingTag.OrganicAndMore]: 'Organic & more',
  [MerchandisingTag.OrderAgain]: 'Order again',
  [MerchandisingTag.CulinaryPicks]: 'Culinary picks',
  [MerchandisingTag.LimitedTime]: 'Limited time',
  [MerchandisingTag.HighProtein]: 'High protein',
  [MerchandisingTag.LowCalorie]: 'Low calorie',
};

/**
 * Maps MerchandisingTag enum values to ForYouCollectionIds enum values.
 * This is used to convert merchandising tags to collection IDs for analytics tracking.
 */
export const MERCHANDISING_TAG_TO_COLLECTION_ID: Partial<Record<MerchandisingTag, ForYouCollectionIds>> = {
  [MerchandisingTag.FallFavorites]: ForYouCollectionIds.FallFavorites,
  [MerchandisingTag.WinterFavorites]: ForYouCollectionIds.WinterFavorites,
  [MerchandisingTag.FamilyFriendly]: ForYouCollectionIds.KidFriendly,
  [MerchandisingTag.NewFresh]: ForYouCollectionIds.NewAndFresh,
  [MerchandisingTag.OrganicAndMore]: ForYouCollectionIds.OrganicAndMore,
  [MerchandisingTag.OrderAgain]: ForYouCollectionIds.OrderAgain,
  [MerchandisingTag.CulinaryPicks]: ForYouCollectionIds.StaffPicks,
  [MerchandisingTag.LimitedTime]: ForYouCollectionIds.LimitedTime,
  [MerchandisingTag.HighProtein]: ForYouCollectionIds.HighProtein,
  [MerchandisingTag.LowCalorie]: ForYouCollectionIds.LowCalorie,
};
